import { RouteRecordRaw, createRouter, createWebHistory } from 'vue-router'

import { saveScrollPosition } from '@/libs/scrollRestoration'
import {
  DefaultTheme,
  RouteThemeDefinition,
  RouteThemeQueryParam,
} from '@/pages/SurveyPage/types/theme'
import { isKnownSymptom } from '@/pages/Symptoms/shared/types'
import { AUTH_CALLBACK_NAME, maybeAuthGuard } from './auth'
import { maybeReloadGuard } from './autoupdate'
import { identityGuard } from './identityGuard'
import { pageTitleHook } from './pageTitle'

let _isPopstateNavigation = false

// Detect popstate (back/forward) before Vue Router processes the navigation.
// scrollBehavior runs AFTER component mount, so it's too late to set a flag there.
// The popstate event fires BEFORE beforeEach guards, so it's reliable for detection.
window.addEventListener('popstate', () => {
  _isPopstateNavigation = true
})

/**
 * Returns true if the current navigation was triggered by
 * browser back/forward (popstate), false for push navigations.
 */
export function isPopstateNavigation() {
  return _isPopstateNavigation
}

const DEFAULT_PAGE_TITLE = 'Thyme Care Connect'

export type Route = RouteRecordRaw & {
  name: string
  meta: {
    unauthenticated?: boolean
    themeDefinition?: RouteThemeDefinition
    title: string
  }
}

export const routes: Route[] = [
  {
    path: '/',
    name: '#home',
    component: () => import('@/pages/HomePage/HomePage.vue'),
    meta: {
      title: DEFAULT_PAGE_TITLE,
    },
  },
  {
    path: '/login-method',
    name: '#loginMethod',
    component: () => import('@/pages/LoginMethod/LoginMethod.vue'),
    meta: {
      unauthenticated: true,
      title: 'Choose Sign In Method',
    },
  },
  {
    path: '/auth/callback',
    name: AUTH_CALLBACK_NAME,
    component: () =>
      import('@/pages/LoginMethod/AuthCallback/AuthCallback.vue'),
    meta: {
      // The auth callback page needs to be public
      // https://github.com/auth0/auth0-vue/blob/main/FAQ.md#4-getting-an-infinite-redirect-loop-between-my-application-and-auth0
      unauthenticated: true,
      title: 'Authenticating...',
    },
  },
  {
    path: '/support',
    name: '#support',
    component: () => import('@/pages/GetSupport/GetSupport.vue'),
    meta: {
      title: 'Get Support',
    },
  },
  {
    path: '/goals',
    name: '#goals',
    component: () => import('@/pages/CareGoals/CareGoals.vue'),
    meta: {
      title: 'Care Goals',
    },
  },
  {
    path: '/nav-team',
    name: '#navTeam',
    component: () => import('@/pages/NavigationTeam/NavigationTeam.vue'),
    meta: {
      title: 'Navigation Team',
    },
  },
  {
    path: '/caregoals',
    name: '#caregoals',
    redirect: () => {
      return { path: '/goals' }
    },
    meta: {
      title: 'Care Goals',
    },
  },
  {
    path: '/goals/:goalId',
    name: '#goal',
    component: () => import('@/pages/CareGoals/GoalDetail/GoalDetail.vue'),
    props: true,
    meta: {
      title: 'Goal',
    },
  },
  {
    path: '/symptoms',
    name: '#symptoms',
    component: () => import('@/pages/Symptoms/TrackSymptoms/TrackSymptoms.vue'),
    meta: {
      title: 'Track Symptoms',
    },
  },
  {
    path: '/symptom-history',
    name: '#symptomHistory',
    component: () =>
      import('@/pages/Symptoms/SymptomHistory/SymptomHistory.vue'),
    meta: {
      title: 'Symptom History',
    },
  },
  {
    path: '/symptom-details/:symptomKey',
    name: '#symptomDetails',
    component: () =>
      import('@/pages/Symptoms/SymptomDetails/SymptomDetails.vue'),
    props: true,
    beforeEnter: (to) => {
      const { symptomKey } = to.params
      if (typeof symptomKey !== 'string' || !isKnownSymptom(symptomKey)) {
        return { name: '#notFound' }
      }
    },
    meta: {
      title: 'Symptom Details',
    },
  },
  {
    path: '/timeline-activities',
    name: '#timelineActivities',
    component: () =>
      import('@/pages/TimelineActivities/TimelineActivities.vue'),
    meta: {
      title: 'Call History',
    },
  },
  {
    path: '/welcome',
    name: '#welcome',
    redirect: () => {
      return { path: '/library/welcome' }
    },
    meta: {
      title: 'Welcome Landing',
    },
  },
  {
    path: '/library',
    name: '#library',
    component: () => import('@/pages/LibraryArticles/LibraryArticles.vue'),
    beforeEnter: identityGuard,
    meta: {
      unauthenticated: true,
      title: 'Library',
    },
  },
  {
    path: '/library/:resourceSlug',
    name: '#libraryPage',
    component: () =>
      import('@/pages/LibraryArticles/LibraryArticle/LibraryArticle.vue'),
    beforeEnter: identityGuard,
    props: true,
    meta: {
      unauthenticated: true,
      title: 'Library',
    },
  },
  {
    path: '/community',
    name: '#communityResources',
    component: () =>
      import('@/pages/CommunityResources/CommunityResources.vue'),
    meta: {
      title: 'Community Resources',
    },
  },
  {
    path: '/ask-thyme',
    name: '#askThyme',
    component: () => import('@/pages/AskThyme/AskThyme.vue'),
    meta: {
      title: 'Ask Thyme',
    },
  },
  // public pages
  {
    path: '/aon/surveys',
    name: '#aonSurveys',
    component: () => import('@/pages/SurveyPage/SurveyPage.vue'),
    meta: {
      unauthenticated: true,
      title: 'AON Survey',
      themeDefinition: {
        themeQueryParams: [RouteThemeQueryParam.PRACTICE],
        default: DefaultTheme.AON,
      },
    },
  },
  {
    path: '/surveys',
    name: '#surveys',
    component: () => import('@/pages/SurveyPage/SurveyPage.vue'),
    meta: {
      unauthenticated: true,
      title: 'Survey',
      themeDefinition: {
        themeQueryParams: [
          RouteThemeQueryParam.PRACTICE,
          RouteThemeQueryParam.CONTRACTING_ENTITY,
        ],
        default: DefaultTheme.DEFAULT,
      },
    },
  },
  {
    path: '/feedback',
    name: '#feedback',
    component: () => import('@/pages/FeedbackPage/FeedbackPage.vue'),
    beforeEnter: identityGuard,
    meta: {
      unauthenticated: true,
      title: 'Feedback',
    },
  },
  {
    path: '/login-help',
    name: '#loginHelp',
    component: () => import('@/pages/LoginMethod/LoginHelp/LoginHelp.vue'),
    meta: {
      unauthenticated: true,
      title: 'Sign In Help',
    },
  },
  {
    path: '/error',
    name: '#error',
    component: () => import('@/tokens/ErrorPage/ErrorPage.vue'),
    meta: {
      unauthenticated: true,
      title: 'Error',
    },
  },
  {
    path: '/complete-onboarding',
    name: '#completeOnboarding',
    component: () =>
      import('@/pages/CompleteOnboarding/CompleteOnboarding.vue'),
    meta: {
      title: 'Complete Onboarding',
    },
  },
  {
    path: '/:pathMatch(.*)*',
    name: '#notFound',
    component: () => import('@/tokens/NotFoundPage/NotFoundPage.vue'),
    meta: {
      unauthenticated: true,
      title: 'Page Not Found',
    },
  },
]

/**
 * Creates the TC connect router.
 */
export function getRouter() {
  const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes,
    // Return false — we manage scroll on #content-wrapper, not window
    scrollBehavior: () => false,
  })

  // eslint-disable-next-line promise/prefer-await-to-callbacks
  router.onError((error: Error, to) => {
    if (
      error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Importing a module script failed')
    ) {
      window.location.href = to.fullPath
    }
  })

  // Save scroll position of the leaving route before any other guards
  router.beforeEach((_, from) => {
    const el = document.getElementById('content-wrapper')
    if (el) {
      saveScrollPosition(from.fullPath, el.scrollTop)
    }
  })

  router.beforeEach(maybeReloadGuard)
  router.beforeEach(maybeAuthGuard)
  router.afterEach(pageTitleHook)

  // Reset the popstate flag after the component has mounted and read it.
  // setTimeout pushes to the macrotask queue, which runs after all microtasks
  // (nextTick, onMounted) from the navigation cycle have completed.
  router.afterEach(() => {
    setTimeout(() => {
      _isPopstateNavigation = false
    }, 0)
  })

  return router
}

export const router = getRouter()
